// Simplified AuthService for frontend2
// Based on frontend/src/services/auth/auth.service.tsx
import { ContextHolder } from '@frontegg/rest-api';

export interface PlbAuthState {
  sub: string;
  name: string;
  email: string;
  tenant_id: string;
  roles: Array<string>;
  tenant_name: string;
  access_token: string;
  profile_img_url?: string;
  tenants?: Array<{
    tenantId: string;
    name: string;
  }>;
  customClaims?: Record<string, any>;
}

declare global {
  interface Window {
    analytics: any;
    thena: any;
    __PLB_ENV__?: Record<string, any>;
  }
}

export default class AuthService {
  private static instance: AuthService;
  
  authUrl: string;
  authAppUrl: string;
  apiUrl: string | undefined;
  useExternalLogin: boolean;
  feClientId: string | undefined;
  feAppId: string | undefined;
  plbAuthState: PlbAuthState | null = null;
  isCustomDomain: boolean = false;
  ssoLoginUrl: string | null = null;
  ssoLogoutUrl: string | null = null;
  ssoEnabled: boolean = false;
  ssoConfigId: string | null = null;
  oidcEnabled: boolean = false;
  oidcLoginUrl: string | null = null;
  oidcLogoutUrl: string | null = null;
  returnToken: string | null = null;
  legacyAppUrl: string | undefined;
  handoffToken: string | null = null;

  constructor() {
    const host = window.location.host;
    this.useExternalLogin = false;

    // Read env config — injected into HTML by Flask, or fetched via XHR in local dev
    let config = window.__PLB_ENV__;
    if (!config) {
      try {
        const isLocal = host.startsWith('local.') || host.startsWith('localhost')
          || host.endsWith('.local.pliable.boston') || host.includes('.local.pliable.boston:');
        const envInitUrl = isLocal
          ? 'https://local.whetstonedata.com:5501/api/v1/env/init'
          : '/api/v1/env/init';
        const xhr = new XMLHttpRequest();
        xhr.open('GET', envInitUrl, false);
        xhr.send();
        if (xhr.status === 200) {
          config = JSON.parse(xhr.responseText);
        }
      } catch (e) {
        console.warn('[AuthService] env/init fallback failed:', e);
      }
    }

    if (config) {
      this.authUrl = config.fe_auth_url;
      this.authAppUrl = config.app_url;
      this.apiUrl = config.api_url;
      this.feClientId = config.fe_client_id;
      this.feAppId = config.fe_app_id;
      this.legacyAppUrl = config.legacy_app_url;
      this.isCustomDomain = config.is_custom_domain || false;
      this.ssoLoginUrl = config.sso_login_url || null;
      this.ssoLogoutUrl = config.sso_logout_url || null;
      this.ssoEnabled = config.sso_enabled || false;
      this.ssoConfigId = config.sso_config_id || null;
      this.oidcEnabled = config.oidc_enabled || false;
      this.oidcLoginUrl = config.oidc_login_url || null;
      this.oidcLogoutUrl = config.oidc_logout_url || null;
      this.returnToken = config.return_token || null;

      console.log(`[AuthService] env=${config.app_env}, domain=${host}, custom=${this.isCustomDomain}, sso=${this.ssoEnabled}, oidc=${this.oidcEnabled}, ssoUrl=${!!this.ssoLoginUrl}`);

      const params = new URLSearchParams(window.location.search);

      // ?nocustom=1 escape hatch: clear SSO guard and redirect to main domain.
      if (params.get('nocustom') === '1' && this.isCustomDomain) {
        sessionStorage.removeItem('plb_sso_attempted');
        window.location.href = `${this.authAppUrl}/?nocustom=1`;
        return;
      }

      // --- OIDC flow (direct IdP integration, replaces Frontegg SSO) ---
      if (this.oidcEnabled) {
        const oidcParams = new URLSearchParams(window.location.search);
        const oidcCode = oidcParams.get('oidc_code');

        if (oidcCode) {
          // Returning from OIDC callback — exchange code for JWT
          try {
            const exchangeXhr = new XMLHttpRequest();
            exchangeXhr.open('GET', `/api/v1/env/auth-exchange?code=${encodeURIComponent(oidcCode)}`, false);
            exchangeXhr.send();
            if (exchangeXhr.status === 200) {
              const data = JSON.parse(exchangeXhr.responseText);
              if (data.access_token) {
                ContextHolder.setAccessToken(data.access_token);
                this.handoffToken = data.access_token;
                try { sessionStorage.setItem('plb_handoff_token', data.access_token); } catch (e) { /* storage may fail */ }

                // Decode JWT and populate auth state
                try {
                  const payload = JSON.parse(atob(data.access_token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
                  this.setAuthState({
                    sub: payload.sub || '',
                    name: payload.name || '',
                    email: payload.email || '',
                    tenant_id: payload.tenantId || '',
                    roles: (payload.roles || []).map((r: any) => typeof r === 'string' ? r : r.key || ''),
                    tenant_name: payload.tenantId || '',
                    access_token: data.access_token,
                    tenants: [],
                    customClaims: payload.customClaims || {},
                  });
                } catch (e) {
                  console.warn('[AuthService] OIDC JWT decode failed:', e);
                }
              }
            }
          } catch (e) {
            console.warn('[AuthService] OIDC code exchange failed:', e);
          }

          // Clean oidc_code from URL
          oidcParams.delete('oidc_code');
          const clean = oidcParams.toString();
          window.history.replaceState(null, '', window.location.pathname + (clean ? '?' + clean : ''));
        } else {
          // Check for existing handoff token in sessionStorage
          const savedOidcToken = sessionStorage.getItem('plb_handoff_token');
          if (savedOidcToken) {
            ContextHolder.setAccessToken(savedOidcToken);
            this.handoffToken = savedOidcToken;

            // Check if token is expired — if so, redirect to OIDC login
            try {
              const payload = JSON.parse(atob(savedOidcToken.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
              if (payload.exp && payload.exp * 1000 < Date.now()) {
                sessionStorage.removeItem('plb_handoff_token');
                this.handoffToken = null;
                if (this.oidcLoginUrl) {
                  window.location.href = this.oidcLoginUrl;
                  return;
                }
              }
            } catch (e) { /* ignore decode errors */ }
          } else if (this.oidcLoginUrl) {
            // No token at all — redirect to OIDC login
            window.location.href = this.oidcLoginUrl;
            return;
          }
        }
        // Skip the rest of the SSO/Frontegg flow — OIDC handles everything
      }

      // Restore handoff token from sessionStorage (survives reload on same origin).
      // This is set after auth-exchange and persisted so that after the reload
      // triggered by AppContent, Frontegg finds the token already set.
      const savedHandoff = sessionStorage.getItem('plb_handoff_token');
      if (savedHandoff && this.isCustomDomain) {
        ContextHolder.setAccessToken(savedHandoff);
        this.handoffToken = savedHandoff;
      }

      // Check if returning from SSO — sso_return=1 is appended by /env/return.
      const isSsoReturn = params.get('sso_return') === '1';
      const authCode = params.get('auth_code');

      // If we have an auth_code, exchange it for a JWT via same-origin API
      // call. This bypasses Safari's third-party cookie blocking — the JWT
      // is passed through a one-time Redis-backed code instead of cookies.
      if (isSsoReturn && authCode && this.isCustomDomain) {
        try {
          const exchangeXhr = new XMLHttpRequest();
          exchangeXhr.open('GET', `/api/v1/env/auth-exchange?code=${encodeURIComponent(authCode)}`, false);
          exchangeXhr.send();
          if (exchangeXhr.status === 200) {
            const data = JSON.parse(exchangeXhr.responseText);
            if (data.access_token) {
              ContextHolder.setAccessToken(data.access_token);
              this.handoffToken = data.access_token;
              try { sessionStorage.setItem('plb_handoff_token', data.access_token); } catch (e) { /* ITP may block */ }

              // Decode JWT and populate auth state so the app considers the
              // user authenticated even if Frontegg's internal refresh fails.
              // AuthenticatedRootPage checks getAuthState() via hasSystemSession.
              try {
                const payload = JSON.parse(atob(data.access_token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
                this.setAuthState({
                  sub: payload.sub || '',
                  name: payload.name || '',
                  email: payload.email || '',
                  tenant_id: payload.tenantId || '',
                  roles: (payload.roles || []).map((r: any) => typeof r === 'string' ? r : r.key || ''),
                  tenant_name: payload.tenantId || '',
                  access_token: data.access_token,
                  tenants: [],
                  customClaims: payload.customClaims || {},
                });
              } catch (e) {
                console.warn('[AuthService] JWT decode for auth state failed:', e);
              }
            }
          }
        } catch (e) {
          console.warn('[AuthService] Auth code exchange failed:', e);
        }
      }

      // Also restore auth state from handoff token on page reload
      if (this.handoffToken && !this.plbAuthState) {
        try {
          const payload = JSON.parse(atob(this.handoffToken.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
          this.setAuthState({
            sub: payload.sub || '',
            name: payload.name || '',
            email: payload.email || '',
            tenant_id: payload.tenantId || '',
            roles: (payload.roles || []).map((r: any) => typeof r === 'string' ? r : r.key || ''),
            tenant_name: payload.tenantId || '',
            access_token: this.handoffToken,
            tenants: [],
            customClaims: payload.customClaims || {},
          });
        } catch (e) { /* ignore */ }
      }

      if (isSsoReturn && this.isCustomDomain) {
        // Clean SSO params from URL
        params.delete('sso_return');
        params.delete('auth_code');
        const clean = params.toString();
        window.history.replaceState(null, '', window.location.pathname + (clean ? '?' + clean : ''));
      }

      // If SSO is enabled on a custom domain, redirect to sso-bounce BEFORE
      // Frontegg loads. Frontegg embedded mode redirects directly to the IdP,
      // bypassing our flow — so returnTo info never gets stored.
      // sso-bounce sets cookies on the main domain, then 302s to the IdP.
      // Skip this entirely when OIDC is enabled — OIDC handles its own redirects above.
      if (this.isCustomDomain && this.ssoEnabled && this.ssoConfigId && !isSsoReturn && !this.oidcEnabled) {
        // sso_login_url is provided by the server in env config (avoids
        // cross-origin sync XHR which Safari ITP blocks). Fall back to
        // client-side fetch only if the server didn't provide it.
        if (!this.ssoLoginUrl) {
          try {
            const ssoUrl = `${this.authUrl}/team/resources/sso/v1/auth/prelogin/${this.ssoConfigId}`;
            const ssoXhr = new XMLHttpRequest();
            ssoXhr.open('POST', ssoUrl, false);
            ssoXhr.send();
            if (ssoXhr.status >= 200 && ssoXhr.status < 300) {
              const ssoData = JSON.parse(ssoXhr.responseText);
              if (ssoData.address) {
                this.ssoLoginUrl = ssoData.address;
              }
            }
          } catch (e) {
            console.warn('[SSO Prelogin] Error:', e);
          }
        }

        // Redirect directly to IdP login URL (skip sso-bounce).
        // sso-bounce was only needed to set cookies for the return redirect,
        // but now AuthenticatedRootPage uses custom_domain from /api/v1/config.
        if (this.ssoLoginUrl && !sessionStorage.getItem('plb_sso_attempted')) {
          sessionStorage.setItem('plb_sso_attempted', Date.now().toString());
          window.location.href = this.ssoLoginUrl;
        }
      }
    } else {
      // Fallback — will likely fail but at least doesn't crash
      console.warn('[AuthService] No env config available, using fallback');
      this.authUrl = `https://auth.${window.location.host}`;
      this.authAppUrl = `https://${window.location.host}`;
    }
  }

  public static getInstance(): AuthService {
    if (!AuthService.instance) {
      AuthService.instance = new AuthService();
    }
    return AuthService.instance;
  }

  public setAuthState(authState: PlbAuthState) {
    this.plbAuthState = authState;
    this.updateAnalytics(authState);
    this.updateThena(authState);
  }

  private updateThena(authState: PlbAuthState) {
    try {
      window.thena?.setEmail({
        email: authState.email,
        hashedEmail: authState.customClaims?.thena_hmac,
        name: authState.name,
      });
    } catch (ex) {
      console.log(ex);
    }
  }

  private updateAnalytics(authState: PlbAuthState) {
    try {
      // Log rocket needs an identify call on each page load or the session shows up anonymous
      window.analytics?.identify(authState.sub, {
        name: authState.name,
        email: authState.email,
      });
    } catch (ex) {
      console.log(ex);
    }
  }

  public getAuthState(): PlbAuthState | null {
    // First try to get from instance state
    if (this.plbAuthState) {
      return this.plbAuthState;
    }
    
    // Then try sessionStorage
    const sessionState = sessionStorage.getItem('plb_auth_state');
    if (sessionState) {
      try {
        this.plbAuthState = JSON.parse(sessionState);
        return this.plbAuthState;
      } catch (e) {
        console.error('Failed to parse auth state from sessionStorage:', e);
      }
    }
    
    return null;
  }

  public getAccessToken(): string | null {
    // Handoff token from custom domain SSO (Safari ITP workaround)
    if (this.handoffToken) {
      return this.handoffToken;
    }

    // First try to get from instance state
    if (this.plbAuthState?.access_token) {
      return this.plbAuthState.access_token;
    }
    
    // Then try sessionStorage
    const authState = this.getAuthState();
    if (authState?.access_token) {
      return authState.access_token;
    }
    
    // Finally, try to get from Frontegg's store directly
    // Frontegg stores auth state in localStorage/sessionStorage
    try {
      // Try to get from Frontegg's auth state
      const fronteggState = (window as any).__FRONTEGG__?.authState;
      if (fronteggState?.user?.accessToken) {
        return fronteggState.user.accessToken;
      }
      
      // Try localStorage (Frontegg might store it there)
      const fronteggAuth = localStorage.getItem('frontegg_auth');
      if (fronteggAuth) {
        const parsed = JSON.parse(fronteggAuth);
        if (parsed?.user?.accessToken) {
          return parsed.user.accessToken;
        }
      }
    } catch (e) {
      console.debug('Could not get token from Frontegg store:', e);
    }
    
    return null;
  }
}

